import { useContext, useState } from 'react'
import Layout from 'shared/layouts/Main'
import useInit from './hooks/useInit'
import getRows from './utils/getRows'
import DeleteModal from 'shared/components/DeleteModal'
import { apiUrls, tableColumnWidths, roles } from 'consts'
import {
  PagingState,
  SearchState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  SortingState,
  DataTypeProvider,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
  PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4'
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid/dist/dx-react-grid'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import hasAnyRole from 'shared/utils/hasAnyRole'
import getBrandContent from 'utils/getBrandContent'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import Footer from 'shared/components/Footer'
import getPageSize from 'utils/getPageSize'
import { formatDate, stringToDate } from 'utils/dateUtils'
import { ReactReduxContext } from 'react-redux'
import LayoutSwitcher from 'components/LayoutSwitcher'
import { NavLink, useNavigate } from 'react-router-dom'
import { IonIcon } from '@ionic/react'
import { cloudUpload, add } from 'ionicons/icons'
import createPermissionOnBackground from './utils/createPermissionOnBackground'
import { Button } from 'react-bootstrap'

const Permissions: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [rows, setRows] = useState<any>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [filter, setFilter] = useState<object | null>(null)
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)
  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
  const [filterVisible, setFilterVisible] = useState<boolean>(false)

  const { store } = useContext(ReactReduxContext)

   const navigate = useNavigate()

  const columns: { name: string; title: string; encodeHtml?: boolean; getCellValue?: any }[] = [
    { name: 'name', title: 'Name' },
    { name: 'active', title: 'Active' },
    { name: 'action', title: 'Action', encodeHtml: false },
  ]

  const columnWidths: TableColumnWidthInfo[] = [
    { columnName: 'name', width: 'auto' },
    { columnName: 'active', width: tableColumnWidths.active },
    { columnName: 'action', width: tableColumnWidths.action },
  ]

  useInit({
    setRows,
    setDeleteEntryId,
    setShowModal,
    setShowOverlay,
    filter,
    currentCustomer
  })

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className='small with-action min-mobile-width' />
  )
  const isAdmin: boolean = hasAnyRole([roles.admin, roles.superAdmin])

  const [dateFormatter] = useState<any>({
    for: ['last_used_date'],
    formatterComponent: (props) => formatDate(stringToDate(props.row.last_used_date)),
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const SearchPanelInputComponent = (props) => {
    return SearchPanelInput({
      inputProps: props,
      insert: isAdmin ? (
              <>
              <NavLink to={'/import-permissions'} className='btn btn-primary me-3'>
                <IonIcon
                  icon={cloudUpload}
                  className='ionicon'
                  style={{ fontSize: '16px', marginBottom: '-3px' }}
                />
                Import Permissions
              </NavLink>
              <Button className='btn btn-primary me-3'
                onClick={() => {
                      createPermissionOnBackground({
                        navigate,
                        setShowOverlay
                      })
                  }}
              >
                <IonIcon
                  icon={add}
                  className='ionicon'
                  style={{ fontSize: '16px', marginBottom: '-3px' }}
                />
                Add Permission
              </Button>
              </>
            ) : undefined,
    })
  }

  const PagingContainer = (props) => {
    return <PagingPanel.Container {...props} className='table-footer' />
  }

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      headerInsert={
        <>
          {hasAnyRole([roles.superAdmin]) && (
            <HeaderCustomerSelect
              setShowOverlay={setShowOverlay}
              handleCustomerChanged={(customer) => setCurrentCustomer(customer)}
            />
          )}
        </>
      }
      menuBottomInsert={hasAnyRole([roles.superAdmin]) ? <LayoutSwitcher store={store} /> : false}
    >
      <>
        <Grid rows={rows} columns={columns}>
          <DataTypeProvider {...dateFormatter} />
          <SortingState defaultSorting={[{ columnName: 'name', direction: 'asc' }]} />
          <SearchState defaultValue='' />
          <IntegratedFiltering />
          <PagingState defaultCurrentPage={0} pageSize={getPageSize()} />
          <IntegratedSorting />
          <IntegratedPaging />
          <Toolbar />
          <SearchPanel inputComponent={SearchPanelInputComponent} />
          <Table tableComponent={TableComponent} />
          <TableColumnResizing defaultColumnWidths={columnWidths} resizingMode='nextColumn' />
          <TableHeaderRow showSortingControls />
          <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />
        </Grid>

        <DeleteModal
          url={apiUrls.permissions}
          showModal={showModal}
          setShowModal={setShowModal}
          deleteEntryId={deleteEntryId}
          setDeleteEntryId={setDeleteEntryId}
          entityName='Permission'
          getRows={getRows}
          setRows={setRows}
          customConfirmMessage={{
            rows,
            confirmMessage: (row) => {
              let confirmMessage = 'Are you sure you want to delete this Permission'

              const entitiesIncluded: Array<string> = []

              if (row.includes_filter_values) {
                entitiesIncluded.push('Filter Values')
              }

              if (row.includes_sections) {
                entitiesIncluded.push('Sections')
              }

              if (entitiesIncluded.length > 0) {
                confirmMessage += ' with all included ' + entitiesIncluded.join(' and ')
              }

              confirmMessage += '? '

              const includedEntites: Array<string> = []

              if (row.is_included_in_roles) {
                includedEntites.push('Role(s)')
              }

              if (includedEntites.length > 0) {
                confirmMessage +=
                  'Note that this Permission is also included in ' +
                  includedEntites.join(' and ') +
                  '.'
              }

              return confirmMessage
            },
          }}
        />
      </>
    </Layout>
  )
}

export default Permissions
